import type {
  UserId,
  SubscriptionTargetKind,
  SubscriptionState,
  TaskId,
  TaskLinkId,
  TaskLink,
  TimeEntryId,
  DayStr,
} from '../../types';
import { findBlockCycle } from '../../helpers';

import { fanoutLast } from '../fanout';
import { addToIndex, removeFromIndex } from '../indexes';
import { snapshot, taskKeys } from '../internal';
import { logActivity } from '../journal';
import { bump, requireCache } from '../runtime';

export function setSubscription(args: {
  userId?: UserId;
  targetKind: SubscriptionTargetKind;
  targetId: string;
  state: SubscriptionState | null;
}): void {
  const cache = requireCache();
  const userId = args.userId ?? cache.me;
  const key = `${String(userId)}:${args.targetKind}:${args.targetId}`;
  const now = cache.nowMs();
  const existing = cache.subscriptions.get(key);
  if (args.state === null) {
    if (existing === undefined) {
      return;
    }
    cache.subscriptions.delete(key);
    logActivity(cache, {
      mutator: 'subscription.set',
      entityTable: 'subscription',
      entityId: key,
      op: 'delete',
      before: snapshot(existing),
      after: null,
    });
  } else if (existing === undefined) {
    const row = {
      userId,
      targetKind: args.targetKind,
      targetId: args.targetId,
      state: args.state,
      createdAt: now,
      updatedAt: now,
    };
    cache.subscriptions.set(key, row);
    logActivity(cache, {
      mutator: 'subscription.set',
      entityTable: 'subscription',
      entityId: key,
      op: 'insert',
      before: null,
      after: snapshot(row),
    });
  } else {
    const before = { state: existing.state, updatedAt: existing.updatedAt };
    existing.state = args.state;
    existing.updatedAt = now;
    logActivity(cache, {
      mutator: 'subscription.set',
      entityTable: 'subscription',
      entityId: key,
      op: 'update',
      before,
      after: { state: existing.state, updatedAt: existing.updatedAt },
    });
  }
  fanoutLast(cache);
  if (args.targetKind === 'task') {
    bump(...taskKeys(cache, args.targetId as TaskId));
  } else if (args.targetKind === 'project') {
    bump(`board:${args.targetId}`);
  }
}

export function linkTasks(args: {
  id: TaskLinkId;
  kind: 'blocks' | 'relates';
  fromTaskId: TaskId;
  toTaskId: TaskId;
}): TaskLink | null {
  const cache = requireCache();
  if (args.fromTaskId === args.toTaskId) {
    return null;
  }
  let fromTaskId = args.fromTaskId;
  let toTaskId = args.toTaskId;
  if (args.kind === 'relates' && String(fromTaskId) > String(toTaskId)) {
    fromTaskId = args.toTaskId;
    toTaskId = args.fromTaskId;
  }
  if (args.kind === 'blocks') {
    const edges: Array<{ fromTaskId: TaskId; toTaskId: TaskId }> = [];
    for (const link of cache.links.values()) {
      if (link.kind === 'blocks') {
        edges.push({ fromTaskId: link.fromTaskId, toTaskId: link.toTaskId });
      }
    }
    const verdict = findBlockCycle(edges, fromTaskId, toTaskId);
    if (verdict.hasCycle) {
      return null;
    }
  }
  const link = {
    id: args.id,
    kind: args.kind,
    fromTaskId,
    toTaskId,
    creatorId: cache.me,
    createdAt: cache.nowMs(),
  };
  cache.links.set(link.id, link);
  addToIndex(cache, 'taskLink', String(link.id));
  logActivity(cache, {
    mutator: 'tasklink.link',
    entityTable: 'taskLink',
    entityId: String(link.id),
    op: 'insert',
    before: null,
    after: snapshot(link),
    taskIds: fromTaskId === toTaskId ? [fromTaskId] : [fromTaskId, toTaskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, fromTaskId), ...taskKeys(cache, toTaskId));
  return link;
}

export function unlinkTasks(args: { id: TaskLinkId }): void {
  const cache = requireCache();
  const link = cache.links.get(args.id);
  if (link === undefined) {
    return;
  }
  removeFromIndex(cache, 'taskLink', String(link.id));
  cache.links.delete(link.id);
  logActivity(cache, {
    mutator: 'tasklink.unlink',
    entityTable: 'taskLink',
    entityId: String(link.id),
    op: 'delete',
    before: snapshot(link),
    after: null,
    taskIds: link.fromTaskId === link.toTaskId ? [link.fromTaskId] : [link.fromTaskId, link.toTaskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, link.fromTaskId), ...taskKeys(cache, link.toTaskId));
}

export function createTimeEntry(args: {
  id: TimeEntryId;
  taskId: TaskId;
  spentOn: DayStr;
  minutes: number;
  note: string;
}): void {
  const cache = requireCache();
  if (!cache.tasks.has(args.taskId)) {
    throw new Error('Задача не найдена');
  }
  const entry = {
    id: args.id,
    taskId: args.taskId,
    userId: cache.me,
    spentOn: args.spentOn,
    minutes: args.minutes,
    note: args.note,
    createdAt: cache.nowMs(),
  };
  cache.timeEntries.set(entry.id, entry);
  addToIndex(cache, 'timeEntry', String(entry.id));
  logActivity(cache, {
    mutator: 'time.create',
    entityTable: 'timeEntry',
    entityId: String(entry.id),
    op: 'insert',
    before: null,
    after: snapshot(entry),
    taskIds: [args.taskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, args.taskId));
}

export function deleteTimeEntry(args: { id: TimeEntryId }): void {
  const cache = requireCache();
  const entry = cache.timeEntries.get(args.id);
  if (entry === undefined) {
    return;
  }
  removeFromIndex(cache, 'timeEntry', String(entry.id));
  cache.timeEntries.delete(entry.id);
  logActivity(cache, {
    mutator: 'time.delete',
    entityTable: 'timeEntry',
    entityId: String(entry.id),
    op: 'delete',
    before: snapshot(entry),
    after: null,
    taskIds: [entry.taskId],
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, entry.taskId));
}
